/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import uuid from 'uuid/v4';
import { commerceInsightsAttributeRenderer } from '../attribute-renderers';
import { CommerceInsightsLogger } from '../event-loggers';
import { generateEventNameHash, p } from '../helpers';
import {
    ICustomEvent,
    IDataAttributeRenderer,
    IEventLogger,
    IExpLogger,
    IPageViewInfo,
    IRegisterTraceLoggerOptions,
    ITelemetry,
    ITelemetryContext,
    ITelemetryRequestContext,
    ITraceLogger,
    LogLevel,
    NumericLogLevel,
    TelemetryEvent,
    TelemetryEventContent
} from '../interfaces';
import { IDictionary } from '../interfaces/IDictionary';
import { ITrackDependency } from '../interfaces/ITrackDependency';
import { InternalTelemetryLogOptions } from './internal-telemetry-log-options';
import { TelemetryLogOptions } from './telemetry-log-options';

/**
 * Base class for Telemetry & Internal telemetry
 * @abstract
 * @class TelemetryBase
 */
export abstract class TelemetryBase {
    /**
     * Enable Data Analytics feature switch
     */
    protected _enableDataAnalytics: boolean;

    /**
     * Map of timers
     */
    protected _timers: Map<string, number>;

    /**
     * Array of ILogger objects representing the trace loggers used.
     */
    protected _traceLoggers: ITraceLogger[];

    /**
     * An object holding the information about telemetry event loggers.
     */
    protected _eventLoggers: IEventLogger[];

    /**
     * An object holding the information about the experimentation logger.
     */
    protected _expLogger: IExpLogger | undefined;

    /**
     * Array holding the registered Data Attribute Renderers
     */
    protected _attributeRenderers: IDataAttributeRenderer[];

    /**
     * Context object used with telemetry.
     */
    protected _telemetryContext: ITelemetryContext;

    /**
     * Dictionary of loggers by name (if one is provided)
     */
    protected _namedLoggers: IDictionary<ITraceLogger>;

    /**
     * WebAnalytics client used to send BI events
     */
    protected _webAnalyticsPlugin?: object;

    public static generateGuid = (): string => {
        return uuid();
    };

    public constructor() {
        this._enableDataAnalytics = false;
        this._traceLoggers = [];
        this._eventLoggers = [];
        this._attributeRenderers = [];
        this._telemetryContext = {
            requestContext: {},
            moduleContext: {}
        };
        this._namedLoggers = {};
        this._timers = new Map<string, number>();
    }

    /**
     * Start a named timer
     *
     * @param {string} id of timer
     * @memberof InternalTelemetry
     */
    public startTimer = (timerId: string): void => {
        this._timers.set(timerId, Date.now());
    };

    /**
     * Stops a named timer
     *
     * @param {string} timerId id of timer
     * @param {boolean} [removeTimer=true] should time be removed from dictionary? default rue
     * @returns {number} elapsed time in ms
     */
    public stopTimer = (timerId: string, removeTimer: boolean = true): number => {
        const now = Date.now();
        const result: number = this._timers.has(timerId) ? now - <number>this._timers.get(timerId) : -1;
        if (removeTimer) {
            this._timers.delete(timerId);
        }
        return result;
    };

    /**
     * Saves the feature swtich marking whether data analytics is enabled for this request
     */
    public enableDataAnalytics = (enableDataAnalytics: boolean): void => {
        this._enableDataAnalytics = enableDataAnalytics;

        if (this._enableDataAnalytics) {
            this._eventLoggers = [new CommerceInsightsLogger()];
            this._attributeRenderers = [commerceInsightsAttributeRenderer];
        }
    };

    /**
     * Renders a data attribute string
     */
    public setTelemetryAttribute = (telemetryId: string, additionalTelemetryData?: object): IDictionary<string> => {
        let telemetryAttribute: IDictionary<string> = {};

        for (let i = 0; i < this._attributeRenderers.length; ++i) {
            telemetryAttribute = { ...telemetryAttribute, ...this._attributeRenderers[i](telemetryId, additionalTelemetryData) };
        }

        return telemetryAttribute;
    };

    /**
     * Logs a telemetry event.
     */
    public logEvent = (eventType: TelemetryEvent, payload: TelemetryEventContent): void => {
        for (let i = 0; i < this._eventLoggers.length; ++i) {
            p(() => this._eventLoggers[i].logEvent(eventType, payload));
        }

        // If exp logger is registered, mutate and send event
        if (this._expLogger) {
            let eventName: string | undefined = '';
            switch (eventType) {
                case TelemetryEvent.PageView:
                    const pageViewEventName = (<IPageViewInfo>payload).title ? `${(<IPageViewInfo>payload).title}.PageView` : '';
                    eventName = pageViewEventName;
                    break;
                case TelemetryEvent.Custom:
                    eventName = this._generateEventName(payload);
                    if (eventName) {
                        break;
                    }
                default:
                    eventName = eventType;
            }
            const eventNameHash = generateEventNameHash(eventName);
            this._expLogger.logEvent(eventNameHash, payload);
        }
    };

    /**
     * Tracks a metric
     */
    public trackMetric = (name: string, value: number): void => {
        this.notifyTraceLoggers(logger => logger.trackMetric(name, value, this._telemetryContext));
    };

    /**
     * Tracks an event
     */
    public trackEvent = <TProperties, TMeasurements>(
        name: string,
        properties?: TProperties,
        measurements?: TMeasurements,
        context?: ITelemetryContext
    ): void => {
        this.notifyTraceLoggers(logger => logger.trackEvent<TProperties, TMeasurements>(name, properties, measurements, context));
    };

    public trackPageView = (page: string, url?: string) => {
        this.notifyTraceLoggers(logger => logger.trackPageView(page, url));
    };

    public trackDependency = (options: ITrackDependency) => {
        this.notifyTraceLoggers(logger => logger.trackDependency(options));
    };

    /**
     * Adds the given trace logger to the list of trace loggers.
     * @param traceLogger - The ILogger object to be added to the list of trace loggers
     */
    public registerTraceLogger = (options: IRegisterTraceLoggerOptions): void => {
        if (options.traceLogger) {
            if (options.traceLogger.initialize && options.options) {
                options.traceLogger.initialize(options.options);
            }
            this._traceLoggers.push(options.traceLogger);
            options.name && (this._namedLoggers[options.name] = options.traceLogger);
        }
    };

    /**
     * Adds the given event logger to the list of event loggers
     * @param eventLogger - The IEventLogger object to be added to the list of event loggers
     */
    public registerEventLogger = (eventLogger: IEventLogger): void => {
        this._eventLoggers.push(eventLogger);
    };

    /**
     * Adds the given experimentation logger to the telemetry object
     * @param expLogger - The IExpLogger object to be added
     */
    public registerExpLogger = (expLogger: IExpLogger): void => {
        this._expLogger = expLogger;
    };

    /**
     * Adds the given attribute renderer to the list of attribute renderers
     * @param attributeRenderer - The IDataAttributeRenderer object to be added to the list of attribute renderers
     */
    public registerAttributeRenderer = (attributeRenderer: IDataAttributeRenderer): void => {
        this._attributeRenderers.push(attributeRenderer);
    };

    /**
     * Registers the Web Analytics plugin for use in the BI logging framework
     */
    public registerWebAnalyticsPlugin = (webAnalyticsPlugin: object): void => {
        this._webAnalyticsPlugin = webAnalyticsPlugin;

        // Pass Web Analytics plugin to the Commerce Insights logger
        if (this._enableDataAnalytics) {
            (<CommerceInsightsLogger>this._eventLoggers[0]).registerWebAnalyticsPlugin(webAnalyticsPlugin);
        }
    };

    /**
     * Merges the properties specific to the current request that should be added to every log
     * @param requestContext Contains the context items specific to the current request
     */
    public setTelemetryRequestContext = (requestContext: Partial<ITelemetryRequestContext>): void => {
        this._telemetryContext.requestContext = { ...(this._telemetryContext.requestContext || {}), ...requestContext };
    };

    /**
     * Gets named logger if it exists
     *
     * @param {string} name name of logger to get
     */
    public getNamedLoggerInstance = <T extends unknown = ITelemetry>(name: string): T | undefined => {
        return <T>this._namedLoggers[name];
    };

    /**
     * String formatter
     * @param templateString Template string
     * @param args ordered argument list
     */
    public stringFormat = (templateString: string, args: string[]): string => {
        for (let index = 0; index < args.length; ++index) {
            templateString = templateString.replace(`$${index}`, args[index]);
        }
        return templateString;
    };

    /**
     * Helper to notify trace loggers
     *
     * @private
     * @memberof Telemetry
     */
    protected notifyTraceLoggers = (fx: (logger: ITraceLogger) => void) => {
        for (let i = 0; i < this._traceLoggers.length; ++i) {
            // TODO: revisit this unhandled promise
            fx(this._traceLoggers[i]);
        }
    };

    protected _log = (
        logLevel: LogLevel,
        messageTemplate: string,
        logOptions?: InternalTelemetryLogOptions | TelemetryLogOptions
    ): void => {
        const requestedLogLevel = NumericLogLevel[logLevel];
        const minLogLevel = <number>(<unknown>process.env.SDK_MIN_LOG_LEVEL);

        // Do not log if below minimum log level
        if (requestedLogLevel < minLogLevel) {
            return;
        }

        // Create map of placeholders in string and arguments provided
        const regex = /{([a-zA-Z_0123456789]+)}/g;
        const placeholders = new Map();
        const values = logOptions && logOptions.values ? logOptions.values : [];
        let i = 0;
        let match = regex.exec(messageTemplate);

        while (match !== null) {
            // If this placeholder is a repeat of a previous one
            if (placeholders.get(match[1]) !== undefined) {
                match = regex.exec(messageTemplate);
                // Else if a new placeholder but no more arguments were given
            } else if (values[i] === undefined) {
                values[i] = `Undefined - No value given for this placeholder`;
                // Still increment in case a placeholder value in the middle was undefined
                placeholders.set(match[1], values[i++]);
                // Get next match
                match = regex.exec(messageTemplate);
                // Else save match and next argument provided
            } else {
                placeholders.set(match[1], values[i++]);

                // Get next match
                match = regex.exec(messageTemplate);
            }
        }
        this.notifyTraceLoggers((logger: ITraceLogger) => {
            logger.sendLog(
                logLevel,
                this._telemetryContext,
                messageTemplate,
                placeholders,
                logOptions && logOptions.customTags,
                logOptions && logOptions.exception
            );
        });
    };

    /**
     * Generates the event name for a custom click event
     * @param payload Payload of custom event
     */
    private _generateEventName(payload: TelemetryEventContent): string | undefined {
        if ((<ICustomEvent>payload).contentCategory === 'click') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const attributes: any = (<ICustomEvent>payload).contentAction;

            // Generate event name for custom clicks with page name, module name, and either element text or recordId
            if (attributes && attributes.pgname && attributes.mname && (attributes.etext || attributes.recid)) {
                return `${attributes.pgname}.${attributes.mname}.${attributes.etext || attributes.recid}${
                    attributes.etype ? `.${attributes.etype}` : ''
                }`;
            }
        }
    }
}
